import { useMemo, useState } from 'react';
import { Row, Col, Card, Form, Button, Alert, Badge, ProgressBar, Spinner } from 'react-bootstrap';
import axios from 'axios';
import PhaseForm, { Num, Txt, Sel, ColorSel } from '../../components/standard/PhaseForms';
import Phase3D, { beddingKeyOf } from '../../components/standard/Phase3D';
import { buildCableModel } from '../../helpers/cableGeometry';
import {
  PHASE_ORDER,
  PHASE_LABEL,
  emptyPhase,
  buildStandardPayload,
  validateStandard
} from '../../helpers/standardPayload';
import {
  buildPrediction,
  applyAllPredictions,
  findStalePhases,
  findStaleDerivations,
  recomputeDerived
} from '../../helpers/standardCascade';
import { CABLE_NAMES, SHEATH_COLORS, SINGLE_CONDUCTOR_NAMES } from '../../data/standardOptions';
import { API } from '../../config/api';
import { suggestInternationalName } from '../../helpers/internationalName';
import { formatProductLabel, formatCoreSpec } from '../../helpers/productLabel';

const API_URL = `${API.BACKEND}/api/v1/standard`;

const emptyHeader = {
  name: '',
  internationalName: '',
  numberOfConductors: null,
  section: null,
  hasMixedConductorSections: false,
  greenYellowSection: null,
  productStandard: '',
  performanceStandard: '',
  notifiedBody: '',
  dop: '',
  tension: '',
  fireReaction: '',
  color: ''
};

/**
 * Building a standard, one layer at a time.
 *
 * Deliberately simpler than the product wizard: a standard is a description, not
 * a plan, so there is nothing to schedule, allocate or reserve. What it does
 * share is the shape of its output — see helpers/standardPayload.js, which is
 * pinned by a test against real standards, because a dozen consumers read this
 * document and none of them validate it.
 *
 * The order is: identify the cable, choose which phases it has, then fill each
 * phase in with the cascade suggesting every diameter as it goes.
 */
const CreateStandardWizard = () => {
  const [step, setStep] = useState(0);
  const [header, setHeader] = useState(emptyHeader);
  const [phases, setPhases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  const setH = patch => setHeader(h => ({ ...h, ...patch }));

  // A suggestion, never an answer. MYYM is the only family with a rule, and
  // inventing a plausible designation for one that has none is worse than
  // leaving it blank — a wrong code on export paperwork is a rejected shipment.
  const suggested = useMemo(
    () => suggestInternationalName(header.name, header.section),
    [header.name, header.section]
  );

  // FY and MYF are sold as the insulated conductor itself, so that phase is
  // where the job ends and the launcher needs to know it. The old form set this
  // when the phase was added and never cleared it, which left the flag behind
  // if the cable type was corrected afterwards — so it is derived from the name
  // instead, in both directions.
  const setName = name =>
    setHeader(h => {
      setPhases(prev =>
        prev.map(p =>
          p.name === 'conductor_isolation'
            ? { ...p, lastPhase: SINGLE_CONDUCTOR_NAMES.includes(name) }
            : p
        )
      );
      return { ...h, name };
    });

  const { pred } = useMemo(
    () => buildPrediction({ phases, numberOfConductors: header.numberOfConductors }),
    [phases, header.numberOfConductors]
  );

  const stale = useMemo(
    () => findStalePhases(phases, header.numberOfConductors),
    [phases, header.numberOfConductors]
  );

  // Phases whose filling no longer follows from the diameters written down —
  // what happens when you go back and widen a conductor after the sheaths are
  // done. Shown on every step, not just the review, because by the review it is
  // too late to remember which value you changed.
  const outOfDate = useMemo(
    () => findStaleDerivations(phases, header.numberOfConductors),
    [phases, header.numberOfConductors]
  );

  const problems = useMemo(() => validateStandard(header, phases), [header, phases]);

  // The same engine Standard Analytics draws from, fed the half-finished cable
  // as it stands. It reads a Standard document, and the wizard's state already
  // IS one — so nothing is converted, and what you see is what will be saved.
  // Guarded because a phase mid-typing is a shape the engine has never been
  // promised, and a throw here would take the whole step down with it.
  const model = useMemo(() => {
    try {
      return buildCableModel({ ...header, phases }, { length: 1000, layLength: 0 });
    } catch {
      return null;
    }
  }, [header, phases]);

  const beddingKey = useMemo(() => (model ? beddingKeyOf(model) : null), [model]);

  // Step 0 is the header, step 1 the phase choice, then one step per phase,
  // and the last is the review.
  const steps = useMemo(
    () => [
      { key: 'header', title: 'Cable' },
      { key: 'phases', title: 'Production phases' },
      ...phases.map(p => ({ key: p.name, title: PHASE_LABEL[p.name] || p.name })),
      { key: 'review', title: 'Review & save' }
    ],
    [phases]
  );

  const current = steps[Math.min(step, steps.length - 1)];
  const phaseIndex = step - 2;

  const headerReady =
    String(header.name || '').trim() &&
    Number(header.numberOfConductors) > 0 &&
    Number(header.section) > 0 &&
    (!header.hasMixedConductorSections || Number(header.greenYellowSection) > 0);

  const togglePhase = name =>
    setPhases(prev => {
      const has = prev.some(p => p.name === name);
      const added = emptyPhase(name);
      if (name === 'conductor_isolation' && SINGLE_CONDUCTOR_NAMES.includes(header.name)) {
        added.lastPhase = true;
      }
      const next = has ? prev.filter(p => p.name !== name) : [...prev, added];
      return next.sort((a, b) => PHASE_ORDER.indexOf(a.name) - PHASE_ORDER.indexOf(b.name));
    });

  const setPhase = (index, next) =>
    setPhases(prev => prev.map((p, i) => (i === index ? next : p)));

  const acceptPrediction = index =>
    setPhase(index, { ...phases[index], ...(pred[index] || {}) });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = buildStandardPayload(header, phases);
      const { data } = await axios.post(API_URL, payload);
      setSaved(data?.data || payload);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setHeader(emptyHeader);
    setPhases([]);
    setStep(0);
    setSaved(null);
    setError(null);
  };

  if (saved) {
    return (
      <Card>
        <Card.Body className="text-center py-6">
          <h3 className="mb-3">Standard saved</h3>
          <p className="text-700">
            {formatProductLabel(saved)}
            {saved.color ? ` · ${saved.color}` : ''} — {saved.phases?.length} phase(s)
          </p>
          <Button variant="primary" onClick={reset}>
            Create another
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Row className="gy-3 mb-4 align-items-center justify-content-between">
        <Col xl={7}>
          <h2 className="mb-2 text-1100">New standard</h2>
          <h5 className="text-700 fw-semi-bold mb-0">
            {header.name || 'Untitled'}
            {header.numberOfConductors && header.section
              ? ` ${formatCoreSpec(header)}`
              : ''}
            {header.color ? ` · ${header.color}` : ''}
          </h5>
        </Col>
        <Col xl={5}>
          <ProgressBar
            now={((step + 1) / steps.length) * 100}
            label={`${step + 1} / ${steps.length}`}
            style={{ height: 22 }}
          />
        </Col>
      </Row>

      <div className="d-flex flex-wrap gap-2 mb-4">
        {steps.map((s, i) => (
          <Badge
            key={s.key + i}
            bg={i === step ? 'primary' : i < step ? 'success' : 'secondary'}
            className="fs-10 py-2 px-3"
            role="button"
            onClick={() => (i <= step || headerReady) && setStep(i)}
          >
            {i + 1}. {s.title}
          </Badge>
        ))}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {outOfDate.length > 0 && (
        <Alert variant="warning" className="fs-9 d-flex justify-content-between align-items-center gap-3">
          <div>
            <strong>{outOfDate.length}</strong>{' '}
            {outOfDate.length === 1 ? 'fază nu mai are' : 'faze nu mai au'} secțiunea de umplere în
            acord cu diametrele scrise — s-a schimbat ceva dedesubt după ce au fost completate.
            <div className="text-700 mt-1">
              {outOfDate.map(s => (
                <div key={`${s.index}-${s.conductorIndex ?? ''}`}>
                  · {s.label}: {s.stored.toFixed(2)} → {s.expected.toFixed(2)} mm²
                </div>
              ))}
            </div>
          </div>
          <Button
            size="sm"
            variant="warning"
            className="text-nowrap"
            onClick={() => setPhases(recomputeDerived(phases, header.numberOfConductors))}
          >
            Recalculează
          </Button>
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header className="py-2">
          <h5 className="mb-0 fs-8">{current.title}</h5>
        </Card.Header>
        <Card.Body>
          {/* ── the cable ── */}
          {current.key === 'header' && (
            <Row>
              <Col md={4}>
                <Sel
                  label="Name"
                  value={header.name}
                  onChange={setName}
                  options={CABLE_NAMES}
                  placeholder="Alege tip cablu"
                />
                <Num
                  label="Number of conductors"
                  value={header.numberOfConductors}
                  onChange={v => setH({ numberOfConductors: v })}
                />
                <Num label="Section (mm²)" value={header.section} onChange={v => setH({ section: v })} />
                <Form.Check
                  type="switch"
                  className="fs-9 my-2"
                  label="Mixed conductor sections"
                  checked={header.hasMixedConductorSections}
                  onChange={e => setH({ hasMixedConductorSections: e.target.checked })}
                />
                {header.hasMixedConductorSections && (
                  <Num
                    label="Green-yellow section (mm²)"
                    value={header.greenYellowSection}
                    onChange={v => setH({ greenYellowSection: v })}
                    hint="Part of the key that identifies this standard"
                  />
                )}
              </Col>
              <Col md={4}>
                {/* What this cable is called abroad. Only the NAME differs on
                    an export order — the standards, the notified body and the
                    DoP below are the same certificate for the same cable. */}
                <Txt
                  label="International name"
                  value={header.internationalName}
                  onChange={v => setH({ internationalName: v })}
                  hint={
                    suggested
                      ? `Suggested for ${header.name} ${header.section} mm²: ${suggested}`
                      : 'Printed instead of the name on export orders. Leave blank if there is none.'
                  }
                />
                {suggested && header.internationalName !== suggested && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mb-2 fs-9 text-decoration-none"
                    onClick={() => setH({ internationalName: suggested })}
                  >
                    Use {suggested}
                  </button>
                )}
                <Txt label="Product standard" value={header.productStandard} onChange={v => setH({ productStandard: v })} />
                <Txt label="Performance standard" value={header.performanceStandard} onChange={v => setH({ performanceStandard: v })} />
                <Txt label="Notified body" value={header.notifiedBody} onChange={v => setH({ notifiedBody: v })} />
                <Txt label="DOP" value={header.dop} onChange={v => setH({ dop: v })} />
              </Col>
              <Col md={4}>
                <Txt label="Tension" value={header.tension} onChange={v => setH({ tension: v })} hint="0.6/1 KV" />
                <Txt label="Fire reaction" value={header.fireReaction} onChange={v => setH({ fireReaction: v })} hint="Eca, Dca…" />
                <ColorSel
                  label="Colour"
                  value={header.color}
                  onChange={v => setH({ color: v })}
                  options={SHEATH_COLORS}
                  placeholder="Selecteaza culoarea mantalei exterioare"
                  hint="Part of the standard's identity — GREY and GREY 7004 are different products"
                />
              </Col>
            </Row>
          )}

          {/* ── which phases ── */}
          {current.key === 'phases' && (
            <>
              <p className="text-700 fs-9">
                Choose everything this cable goes through. They are filled in one at a time next, in
                build order, and each phase&apos;s diameter is suggested from the one beneath it.
              </p>
              <Row>
                {PHASE_ORDER.map(name => {
                  const on = phases.some(p => p.name === name);
                  return (
                    <Col md={4} key={name} className="mb-2">
                      <Card
                        role="button"
                        onClick={() => togglePhase(name)}
                        className={on ? 'border-primary' : ''}
                      >
                        <Card.Body className="py-3">
                          <Form.Check
                            type="checkbox"
                            checked={on}
                            readOnly
                            label={<span className="fw-semibold fs-9">{PHASE_LABEL[name]}</span>}
                          />
                          <div className="text-700 fs-10 mt-1">{name}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              {!phases.length && (
                <Alert variant="warning" className="fs-9 mt-3 mb-0">
                  A standard needs at least one phase.
                </Alert>
              )}
            </>
          )}

          {/* ── one phase ── */}
          {phaseIndex >= 0 && phaseIndex < phases.length && (
            <Row className="g-3">
              <Col xxl={8}>
                <PhaseForm
                  phase={phases[phaseIndex]}
                  onChange={next => setPhase(phaseIndex, next)}
                  prediction={pred[phaseIndex]}
                  onAcceptPrediction={() => acceptPrediction(phaseIndex)}
                  phases={phases}
                  index={phaseIndex}
                  numberOfConductors={header.numberOfConductors}
                  hasMixedConductorSections={header.hasMixedConductorSections}
                  section={header.section}
                  greenYellowSection={header.greenYellowSection}
                />
              </Col>
              <Col xxl={4}>
                <div className="position-sticky" style={{ top: 16 }}>
                  <div className="fs-10 text-700 text-uppercase fw-bold mb-1">
                    {PHASE_LABEL[phases[phaseIndex].name] || phases[phaseIndex].name}
                  </div>
                  <Phase3D
                    phase={phases[phaseIndex]}
                    model={model}
                    beddingKey={beddingKey}
                    productColor={header.color}
                    height={320}
                  />
                  <div className="text-700 fs-10 mt-1">
                    Se redesenează la fiecare valoare introdusă — dacă nu arată ca
                    firul din mână, un număr e greșit.
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* ── review ── */}
          {current.key === 'review' && (
            <>
              {problems.length > 0 && (
                <Alert variant="warning" className="fs-9">
                  <div className="fw-bold mb-1">Still missing</div>
                  {problems.map((p, i) => (
                    <div key={i}>· {p.message}</div>
                  ))}
                  <div className="mt-2 text-700">
                    A standard can be saved with gaps, but anything launched from it will inherit
                    them.
                  </div>
                </Alert>
              )}

              {stale.length > 0 && (
                <Alert variant="danger" className="fs-9">
                  <div className="fw-bold mb-1">
                    {stale.length} phase(s) no longer agree with the cascade
                  </div>
                  {stale.map(s => (
                    <div key={s.index}>
                      · {PHASE_LABEL[s.name] || s.name}: stored {s.stored.toFixed(2)} mm, expected{' '}
                      {s.expected.toFixed(2)} mm
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="danger"
                    className="mt-2"
                    onClick={() => setPhases(applyAllPredictions(phases, header.numberOfConductors))}
                  >
                    Apply the cascade to all of them
                  </Button>
                </Alert>
              )}

              <div className="table-responsive">
                <table className="table table-sm fs-9 mb-0">
                  <thead>
                    <tr className="text-700 text-uppercase fs-10">
                      <th>Phase</th>
                      <th>Material</th>
                      <th className="text-end">Ø (mm)</th>
                      <th className="text-end">Section</th>
                      <th className="text-end">Filling</th>
                      <th>Depends on</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildStandardPayload(header, phases).phases.map(p => (
                      <tr key={p.name}>
                        <td className="fw-semibold">{PHASE_LABEL[p.name] || p.name}</td>
                        <td>
                          {p.name === 'conductor_isolation'
                            ? `${p.conductors.length} conductor(s)`
                            : p.name === 'armouring'
                              ? `BLR ${p.wide ?? '—'} × ${p.thickness ?? '—'}`
                              : [p.material, p.type].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="text-end">{p.diameter ?? '—'}</td>
                        <td className="text-end">{p.section ?? '—'}</td>
                        <td className="text-end">{p.fillingSection ?? '—'}</td>
                        <td className="text-700">{p.dependsOn || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center">
        <Button variant="outline-secondary" disabled={step === 0} onClick={() => setStep(s => s - 1)}>
          Back
        </Button>

        <div className="d-flex gap-2 align-items-center">
          {stale.length > 0 && current.key !== 'review' && (
            <Badge bg="danger" className="fs-10">
              {stale.length} phase(s) out of date
            </Badge>
          )}
          {current.key === 'review' ? (
            <Button variant="primary" onClick={save} disabled={saving || !phases.length}>
              {saving && <Spinner size="sm" animation="border" className="me-2" />}
              Save standard
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={
                (current.key === 'header' && !headerReady) ||
                (current.key === 'phases' && !phases.length)
              }
              onClick={() => setStep(s => s + 1)}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateStandardWizard;
